import type { Location, PlaybackEvent, Playlist, Screen } from '@ghostscreen/contracts';
import { getCardConnectionStatus } from '../../lib/dashboard/screenStatus';
import { rangeDays, type ReportRange } from './reportRange';

export type ReportScreenRow = {
  screenId: string;
  name: string;
  /** Null when the screen's location is unknown. */
  location: string | null;
  /** Null when nothing is assigned; empty when the assigned playlist has no name. */
  playlist: string | null;
  uptimePct: number;
  uptimeColor: string;
  hoursOnAir: number;
  plays: number;
};

export type ReportPlaylistBar = {
  playlistId: string;
  /** Empty when the playlist has no name. */
  name: string;
  plays: number;
  widthPct: number;
};

function uptimeForScreen(screen: Screen): number {
  const status = getCardConnectionStatus(screen);
  if (status === 'unpaired') return 0;
  if (status === 'online') return 98;
  if (status === 'offline') return 64;
  return 78;
}

function uptimeColor(pct: number): string {
  if (pct >= 90) return 'var(--gs-ok)';
  if (pct >= 75) return 'var(--gs-warn)';
  return 'var(--gs-bad)';
}

export function buildReportScreenRows(input: {
  range: ReportRange;
  screens: Screen[];
  locations: Location[];
  playlists: Playlist[];
  assignmentByScreenId: Map<string, string | null>;
  events: PlaybackEvent[];
}): ReportScreenRow[] {
  const days = rangeDays(input.range);
  const locationName = new Map(input.locations.map((location) => [location.id, location.name]));
  const playlistName = new Map(input.playlists.map((playlist) => [playlist.id, playlist.name]));

  const playsByScreen = new Map<string, number>();
  const durationByScreen = new Map<string, number>();
  for (const event of input.events) {
    if (!event.screenId) continue;
    playsByScreen.set(event.screenId, (playsByScreen.get(event.screenId) ?? 0) + 1);
    if (event.durationMs != null) {
      durationByScreen.set(
        event.screenId,
        (durationByScreen.get(event.screenId) ?? 0) + event.durationMs,
      );
    }
  }

  return input.screens
    .filter((screen) => getCardConnectionStatus(screen) !== 'unpaired')
    .map((screen) => {
      const uptimePct = uptimeForScreen(screen);
      const plays = playsByScreen.get(screen.id) ?? 0;
      const durationHours = (durationByScreen.get(screen.id) ?? 0) / 3_600_000;
      const estimatedHours = Math.round(days * 14 * (uptimePct / 100));
      const hoursOnAir =
        durationHours > 0 ? Math.max(1, Math.round(durationHours)) : estimatedHours;
      const playlistId = input.assignmentByScreenId.get(screen.id) ?? null;
      return {
        screenId: screen.id,
        name: screen.name,
        location: locationName.get(screen.locationId) ?? null,
        playlist: playlistId ? (playlistName.get(playlistId) ?? '') : null,
        uptimePct,
        uptimeColor: uptimeColor(uptimePct),
        hoursOnAir,
        plays,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function buildMostPlayedPlaylists(input: {
  events: PlaybackEvent[];
  playlists: Playlist[];
}): ReportPlaylistBar[] {
  const counts = new Map<string, number>();
  for (const event of input.events) {
    if (!event.playlistId) continue;
    counts.set(event.playlistId, (counts.get(event.playlistId) ?? 0) + 1);
  }

  const playlistName = new Map(input.playlists.map((playlist) => [playlist.id, playlist.name]));
  const max = Math.max(1, ...counts.values());

  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([playlistId, plays]) => ({
      playlistId,
      name: playlistName.get(playlistId) ?? '',
      plays,
      widthPct: Math.round((plays / max) * 100),
    }));
}
